import { useEffect, useMemo, useState } from 'react'
import {
  ActionIcon,
  Alert,
  Button,
  Card,
  Grid,
  Group,
  Modal,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import {
  IconAlertCircle,
  IconChartBar,
  IconDeviceFloppy,
  IconDownload,
  IconExternalLink,
  IconPlayerPlay,
  IconPlus,
  IconX,
} from '@tabler/icons-react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  createQuery,
  executeStatement,
  getQueryEditorUrl,
  listQueries,
  listWarehouses,
  updateQuery,
  type DatabricksQuery,
  type QueryResult,
  type SqlWarehouse,
} from '../../api/databricks'
import { SchemaExplorer } from './SchemaExplorer'
import { SqlEditor } from './SqlEditor'

const HISTORY_KEY = 'prism-query-history'
const DEFAULT_WAREHOUSE_ID = import.meta.env
  .VITE_DATABRICKS_SQL_WAREHOUSE_ID as string | undefined

function generateId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

function useDatabricksQueries() {
  const [queries, setQueries] = useState<DatabricksQuery[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await listQueries()
      setQueries(data)
    } catch (err) {
      const axiosError = err as {
        response?: { data?: { message?: string } }
      }
      setError(
        axiosError.response?.data?.message ??
          (err instanceof Error
            ? err.message
            : 'Failed to load saved queries from Databricks.')
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
  }, [])

  const save = async (query: DatabricksQuery) => {
    if (query.id) {
      const updated = await updateQuery(query)
      setQueries((prev) => prev.map((q) => (q.id === updated.id ? updated : q)))
      return updated
    }
    const created = await createQuery({
      name: query.name,
      query: query.query,
      description: query.description,
      warehouse_id: query.warehouse_id,
    })
    setQueries((prev) => [...prev, created])
    return created
  }

  return { queries, loading, error, refresh, save }
}

function useWarehouses() {
  const [warehouses, setWarehouses] = useState<SqlWarehouse[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    listWarehouses()
      .then((data) => setWarehouses(data))
      .catch((err) => {
        const axiosError = err as {
          response?: { data?: { message?: string } }
        }
        setError(
          axiosError.response?.data?.message ??
            (err instanceof Error
              ? err.message
              : 'Failed to load SQL warehouses.')
        )
      })
      .finally(() => setLoading(false))
  }, [])

  return { warehouses, loading, error }
}

function useQueryHistory() {
  const [history, setHistory] = useState<string[]>([])

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(HISTORY_KEY) ?? '[]')
      if (Array.isArray(stored)) setHistory(stored)
    } catch {
      // ignore invalid stored history
    }
  }, [])

  const add = (query: string) => {
    setHistory((prev) => {
      const next = [query, ...prev.filter((q) => q !== query)].slice(0, 20)
      localStorage.setItem(HISTORY_KEY, JSON.stringify(next))
      return next
    })
  }

  return { history, add }
}

interface QueryTab {
  id: string
  name: string
  warehouseId: string
  query: string
  variables: Record<string, string>
  result: QueryResult | null
  error: string | null
  loading: boolean
}

function extractVariables(query: string): string[] {
  const names = new Set<string>()
  const pattern = /\{\{(\w+)\}\}/g
  let match: RegExpExecArray | null
  while ((match = pattern.exec(query)) !== null) {
    names.add(match[1])
  }
  return Array.from(names)
}

function substituteVariables(
  query: string,
  variables: Record<string, string>
): string {
  return query.replace(/\{\{(\w+)\}\}/g, (_, name) => {
    const value = variables[name] ?? ''
    return value
  })
}

function downloadCsv(result: QueryResult) {
  const escape = (value: string) => {
    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
      return `"${value.replace(/"/g, '""')}"`
    }
    return value
  }
  const lines: string[] = [
    result.columns.map((c) => c.name).join(','),
    ...result.rows.map((row) =>
      row.map((cell) => escape(cell ?? 'NULL')).join(',')
    ),
  ]
  const csv = lines.join('\n')
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'query-results.csv'
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

function QueryChart({ result }: { result: QueryResult }) {
  const [xKey, setXKey] = useState<string | null>(null)
  const [yKey, setYKey] = useState<string | null>(null)

  const data = useMemo(() => {
    return result.rows.map((row) => {
      const record: Record<string, string | number> = {}
      result.columns.forEach((column, index) => {
        const raw = row[index] ?? ''
        record[column.name] = raw
      })
      return record
    })
  }, [result])

  const chartData = useMemo(() => {
    if (!xKey || !yKey) return []
    return data.map((row) => ({
      ...row,
      [yKey]: Number(row[yKey]) || 0,
    }))
  }, [data, xKey, yKey])

  return (
    <Stack>
      <Group>
        <Select
          label="X axis"
          placeholder="Select column"
          data={result.columns.map((c) => ({
            value: c.name,
            label: `${c.name}${c.typeName ? ` (${c.typeName})` : ''}`,
          }))}
          value={xKey}
          onChange={setXKey}
          style={{ flex: 1 }}
        />
        <Select
          label="Y axis"
          placeholder="Select column"
          data={result.columns.map((c) => ({
            value: c.name,
            label: `${c.name}${c.typeName ? ` (${c.typeName})` : ''}`,
          }))}
          value={yKey}
          onChange={setYKey}
          style={{ flex: 1 }}
        />
      </Group>
      {xKey && yKey && (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={xKey} />
            <YAxis />
            <RechartsTooltip />
            <Bar dataKey={yKey} fill="var(--mantine-color-prism-6)" />
          </BarChart>
        </ResponsiveContainer>
      )}
      {!xKey || !yKey ? (
        <Text c="dimmed" size="sm">
          Select an X and Y column to render a bar chart.
        </Text>
      ) : null}
    </Stack>
  )
}

export function QueryData() {
  const [tabs, setTabs] = useState<QueryTab[]>(() => [
    {
      id: generateId(),
      name: 'Query 1',
      warehouseId: DEFAULT_WAREHOUSE_ID ?? '',
      query: 'SELECT 1',
      variables: {},
      result: null,
      error: null,
      loading: false,
    },
  ])
  const [activeTabId, setActiveTabId] = useState<string>(tabs[0].id)
  const activeTab = tabs.find((t) => t.id === activeTabId) ?? tabs[0]

  const [saveModalOpen, { open: openSaveModal, close: closeSaveModal }] =
    useDisclosure(false)
  const [saveName, setSaveName] = useState('')
  const [overwriteId, setOverwriteId] = useState<string | null>(null)
  const [savedQueryId, setSavedQueryId] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)

  const {
    queries: savedQueries,
    loading: savedQueriesLoading,
    error: savedQueriesError,
    save,
  } = useDatabricksQueries()
  const {
    warehouses,
    loading: warehousesLoading,
    error: warehousesError,
  } = useWarehouses()
  const { history, add } = useQueryHistory()
  const editorUrl = getQueryEditorUrl(savedQueryId ?? undefined)

  const updateActiveTab = (patch: Partial<QueryTab>) => {
    setTabs((prev) =>
      prev.map((t) => (t.id === activeTab.id ? { ...t, ...patch } : t))
    )
  }

  const addTab = () => {
    const newTab: QueryTab = {
      id: generateId(),
      name: `Query ${tabs.length + 1}`,
      warehouseId: DEFAULT_WAREHOUSE_ID ?? '',
      query: '',
      variables: {},
      result: null,
      error: null,
      loading: false,
    }
    setTabs((prev) => [...prev, newTab])
    setActiveTabId(newTab.id)
  }

  const closeTab = (id: string) => {
    setTabs((prev) => {
      if (prev.length === 1) return prev
      const next = prev.filter((t) => t.id !== id)
      if (activeTabId === id) {
        setActiveTabId(next[next.length - 1].id)
      }
      return next
    })
  }

  const runQuery = async () => {
    if (!activeTab.warehouseId.trim()) {
      updateActiveTab({ error: 'Enter a Databricks SQL warehouse ID.' })
      return
    }
    updateActiveTab({ loading: true, error: null, result: null })
    try {
      const finalQuery = substituteVariables(
        activeTab.query,
        activeTab.variables
      )
      const { result: data } = await executeStatement(
        activeTab.warehouseId.trim(),
        finalQuery
      )
      updateActiveTab({ result: data, loading: false })
      add(activeTab.query)
    } catch (err) {
      const axiosError = err as {
        response?: { data?: { message?: string } }
      }
      updateActiveTab({
        error:
          axiosError.response?.data?.message ??
          (err instanceof Error ? err.message : 'Query execution failed.'),
        loading: false,
      })
    }
  }

  const activeVariables = extractVariables(activeTab.query)

  return (
    <Stack>
      <Group justify="space-between" align="flex-end">
        <Stack gap={0}>
          <Title order={1}>Query Data</Title>
          <Text c="dimmed">Run SQL queries against the data warehouse.</Text>
        </Stack>
      </Group>
      <Grid>
        <Grid.Col span={{ base: 12, md: 3 }}>
          <SchemaExplorer
            onInsert={(name) =>
              updateActiveTab({
                query: activeTab.query ? `${activeTab.query}\n${name}` : name,
              })
            }
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 9 }}>
          <Stack>
            <Card withBorder>
              <Stack>
                <Group gap="xs">
                  {tabs.map((tab) => (
                    <Button
                      key={tab.id}
                      variant={tab.id === activeTabId ? 'filled' : 'light'}
                      size="xs"
                      onClick={() => setActiveTabId(tab.id)}
                      rightSection={
                        <ActionIcon
                          size="xs"
                          variant="transparent"
                          onClick={(e) => {
                            e.stopPropagation()
                            closeTab(tab.id)
                          }}
                        >
                          <IconX size={12} />
                        </ActionIcon>
                      }
                    >
                      {tab.name}
                    </Button>
                  ))}
                  <Tooltip label="New query tab">
                    <ActionIcon onClick={addTab}>
                      <IconPlus size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
                <Group align="flex-end">
                  {warehouses.length > 0 ? (
                    <Select
                      label="SQL warehouse"
                      placeholder="Select a warehouse"
                      data={warehouses.map((warehouse) => ({
                        value: warehouse.id,
                        label: warehouse.name,
                      }))}
                      value={activeTab.warehouseId}
                      onChange={(value) =>
                        updateActiveTab({ warehouseId: value ?? '' })
                      }
                      style={{ flex: 1 }}
                      disabled={warehousesLoading}
                      error={warehousesError ?? undefined}
                    />
                  ) : (
                    <TextInput
                      label="SQL warehouse ID"
                      placeholder="e.g. 1234567890abcdef"
                      value={activeTab.warehouseId}
                      onChange={(event) =>
                        updateActiveTab({
                          warehouseId: event.currentTarget.value,
                        })
                      }
                      style={{ flex: 1 }}
                      disabled={warehousesLoading}
                      error={warehousesError ?? undefined}
                    />
                  )}
                  <Select
                    label="Saved queries"
                    placeholder="Load a saved query"
                    data={savedQueries.map((q) => ({
                      value: q.id,
                      label: q.name,
                    }))}
                    value={savedQueryId}
                    onChange={(value) => {
                      const selected = savedQueries.find((q) => q.id === value)
                      if (selected) {
                        setSavedQueryId(selected.id)
                        updateActiveTab({
                          query: selected.query,
                          warehouseId:
                            selected.warehouse_id ?? activeTab.warehouseId,
                        })
                      } else {
                        setSavedQueryId(null)
                      }
                    }}
                    clearable
                    disabled={savedQueriesLoading}
                    error={savedQueriesError ?? undefined}
                  />
                  {history.length > 0 && (
                    <Select
                      label="Recent queries"
                      placeholder="Load a recent query"
                      data={history.map((q, index) => ({
                        value: index.toString(),
                        label: q.length > 60 ? `${q.slice(0, 60)}…` : q,
                      }))}
                      onChange={(value) => {
                        if (value) {
                          updateActiveTab({ query: history[Number(value)] })
                        }
                      }}
                      clearable
                    />
                  )}
                </Group>
                <Text size="sm" fw={700}>
                  SQL query
                </Text>
                <SqlEditor
                  value={activeTab.query}
                  onChange={(value) => updateActiveTab({ query: value })}
                  height="300px"
                />
                {activeVariables.length > 0 && (
                  <Card withBorder p="sm">
                    <Text size="sm" fw={700} mb="xs">
                      Variables
                    </Text>
                    <Group align="flex-end">
                      {activeVariables.map((name) => (
                        <TextInput
                          key={name}
                          label={`{{${name}}}`}
                          value={activeTab.variables[name] ?? ''}
                          onChange={(event) =>
                            updateActiveTab({
                              variables: {
                                ...activeTab.variables,
                                [name]: event.currentTarget.value,
                              },
                            })
                          }
                          style={{ flex: 1 }}
                        />
                      ))}
                    </Group>
                  </Card>
                )}
                <Group justify="flex-end">
                  <Button
                    variant="light"
                    leftSection={<IconDeviceFloppy size={16} />}
                    onClick={openSaveModal}
                  >
                    Save query
                  </Button>
                  {editorUrl && (
                    <Button
                      component="a"
                      href={editorUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="default"
                      leftSection={<IconExternalLink size={16} />}
                    >
                      Open in Databricks
                    </Button>
                  )}
                  <Button
                    leftSection={<IconPlayerPlay size={16} />}
                    loading={activeTab.loading}
                    onClick={runQuery}
                  >
                    Run query
                  </Button>
                </Group>
              </Stack>
            </Card>
            {activeTab.error && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                color="red"
                title="Error"
              >
                {activeTab.error}
              </Alert>
            )}
            {activeTab.result && (
              <Card withBorder>
                <Tabs defaultValue="results">
                  <Tabs.List>
                    <Tabs.Tab value="results">Results</Tabs.Tab>
                    <Tabs.Tab
                      value="chart"
                      leftSection={<IconChartBar size={14} />}
                    >
                      Chart
                    </Tabs.Tab>
                  </Tabs.List>
                  <Tabs.Panel value="results" pt="sm">
                    <Group justify="space-between" mb="xs">
                      <Text fw={700}>Result</Text>
                      <Group>
                        {activeTab.result.truncated ? (
                          <Text size="sm" c="dimmed">
                            Showing{' '}
                            {activeTab.result.rows.length.toLocaleString()} of{' '}
                            {activeTab.result.totalRowCount.toLocaleString()}{' '}
                            rows
                          </Text>
                        ) : (
                          <Text size="sm" c="dimmed">
                            {activeTab.result.rows.length.toLocaleString()} rows
                          </Text>
                        )}
                        <Button
                          variant="default"
                          size="xs"
                          leftSection={<IconDownload size={14} />}
                          onClick={() => downloadCsv(activeTab.result!)}
                        >
                          Export CSV
                        </Button>
                      </Group>
                    </Group>
                    <Table.ScrollContainer minWidth={500}>
                      <Table striped highlightOnHover>
                        <Table.Thead>
                          <Table.Tr>
                            {activeTab.result.columns.map((column) => (
                              <Table.Th key={column.name}>
                                <Text size="sm" fw={700}>
                                  {column.name}
                                </Text>
                                {column.typeName && (
                                  <Text size="xs" c="dimmed">
                                    {column.typeName}
                                  </Text>
                                )}
                              </Table.Th>
                            ))}
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {activeTab.result.rows.map((row, rowIndex) => (
                            <Table.Tr key={rowIndex}>
                              {row.map((cell, cellIndex) => (
                                <Table.Td key={cellIndex}>
                                  <Text size="sm">{cell ?? 'NULL'}</Text>
                                </Table.Td>
                              ))}
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </Table.ScrollContainer>
                  </Tabs.Panel>
                  <Tabs.Panel value="chart" pt="sm">
                    <QueryChart result={activeTab.result} />
                  </Tabs.Panel>
                </Tabs>
              </Card>
            )}
          </Stack>
        </Grid.Col>
      </Grid>

      <Modal opened={saveModalOpen} onClose={closeSaveModal} title="Save query">
        <Stack>
          <TextInput
            label="Name"
            placeholder="My query"
            value={saveName}
            onChange={(event) => setSaveName(event.currentTarget.value)}
          />
          {savedQueries.length > 0 && (
            <Select
              label="Overwrite existing query"
              placeholder="Create a new saved query"
              data={savedQueries.map((q) => ({
                value: q.id,
                label: q.name,
              }))}
              value={overwriteId}
              onChange={setOverwriteId}
              clearable
            />
          )}
          {saveError && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="red"
              title="Save failed"
            >
              {saveError}
            </Alert>
          )}
          <Group justify="flex-end">
            <Button
              loading={savedQueriesLoading}
              onClick={async () => {
                if (!saveName.trim()) return
                if (!activeTab.warehouseId.trim()) {
                  setSaveError(
                    'Enter a SQL warehouse ID before saving the query.'
                  )
                  return
                }
                setSaveError(null)
                try {
                  let queryToSave: DatabricksQuery
                  if (overwriteId) {
                    const existing = savedQueries.find(
                      (q) => q.id === overwriteId
                    )
                    queryToSave = {
                      ...existing!,
                      name: saveName.trim(),
                      query: activeTab.query,
                      warehouse_id: activeTab.warehouseId,
                    }
                  } else {
                    queryToSave = {
                      id: '',
                      name: saveName.trim(),
                      query: activeTab.query,
                      warehouse_id: activeTab.warehouseId,
                    }
                  }
                  const saved = await save(queryToSave)
                  setSavedQueryId(saved.id)
                  setSaveName('')
                  setOverwriteId(null)
                  closeSaveModal()
                } catch (err) {
                  const axiosError = err as {
                    response?: { data?: { message?: string } }
                  }
                  setSaveError(
                    axiosError.response?.data?.message ??
                      (err instanceof Error
                        ? err.message
                        : 'Failed to save query.')
                  )
                }
              }}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}
